import { GameDocument } from '../../../types/game-document';
import { TaskEntity } from '../../../types/game-document/';
import { TaskContent } from '../../../types/game-document/entities/task-content';
import { GetResourceValue } from '../resources';

/**
 * Get all tasks from game document local storage.
 * @param gameDocument - The Game Document as datasource
 * @constructor
 */
export const GetTasks = (gameDocument: GameDocument | undefined) => {
  return gameDocument?.assets?.tasks ?? [];
};

/**
 * Get tasks by Ids from game document local storage.
 * @param gameDocument - The Game Document as datasource
 * @constructor
 */
export const GetTasksByIds = (
  gameDocument: GameDocument | undefined,
  ids: string[] | undefined
) => {
  return (
    gameDocument?.assets?.tasks?.filter((x) => ids?.indexOf(x.id) !== -1) ?? []
  );
};

/**
 * Get task by Id from game document local storage.
 * @param gameDocument - The Game Document as datasource
 * @param id - The ID you want to search
 * @constructor
 */
export const GetTaskById = (
  gameDocument: GameDocument | undefined,
  id: string
) => {
  return gameDocument?.assets?.tasks?.find((x) => x.id === id);
};

/**
 * Get task timer by task id from game document local storage.
 * @param gameDocument - The Game Document as datasource
 * @param zoneId - The task id you are looking for
 * @constructor
 */
export const GetTaskTimerById = (
  gameDocument: GameDocument | undefined,
  taskId: string
) => {
  return gameDocument?.assets.tasks?.find((x) => x.id === taskId);
};

/**
 * Get tasks by Zone Id from game document local storage.
 * @param gameDocument - The Game Document as datasource
 * @constructor
 */
export const GetTasksByZoneId = (
  zoneId: string,
  gameDocument: GameDocument | undefined
) => {
  let tasks: TaskEntity[] = [];

  if (gameDocument) {
    let zone = gameDocument?.assets?.zones?.find((x) => x.id === zoneId);

    const taskIds = zone?.tasks?.map(function (e) {
      return e.taskAssId;
    });

    if (taskIds) {
      return GetTasksByIds(gameDocument, taskIds);
    }
  }

  return tasks;
};

/**
 * Get task display title by Task and/or TaskContent entity.
 * Resolves title from resources using titleResId with fallback to entity name.
 * @param gameDocument - The Game Document as datasource
 * @param task - The Task entity
 * @param taskContent - The TaskContent entity
 * @param languageId - The language ID
 */
export const GetTaskTitleByEntity = (
  gameDocument: GameDocument | undefined,
  task?: TaskEntity,
  taskContent?: TaskContent,
  languageId?: string
) => {
  if (!gameDocument) return '';

  const content =
    taskContent ??
    (task?.taskContentId
      ? gameDocument?.assets?.taskContents?.find(
          (x) => x.id === task.taskContentId
        )
      : undefined);

  const titleResId = task?.titleResId || content?.titleResId;

  if (titleResId) {
    const value = GetResourceValue(gameDocument, titleResId, languageId ?? '');
    if (value && value.trim() !== '') return value;
  }

  return '';
};

/**
 * Get task display title from game document resources by task id or task content id.
 * @param gameDocument - The Game Document as datasource
 * @param id - The Task ID or Task Content ID
 * @param languageId - The language ID
 */
export const GetTaskTitle = (
  gameDocument: GameDocument | undefined,
  id: string,
  languageId?: string
) => {
  if (!gameDocument || !id) return '';
  const task = GetTaskById(gameDocument, id);
  const taskContent = gameDocument?.assets?.taskContents?.find(
    (x) => x.id === id
  );

  return GetTaskTitleByEntity(gameDocument, task, taskContent, languageId);
};

